import React from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Badge } from '@/Components/ui/badge';
import { Shield, Target, Users, Award } from 'lucide-react';
import SEO from '../Components/SEO.jsx';

const iconMap = {
    storia: Users,
    missione: Target,
    valori: Shield,
    team: Users,
    certificazioni: Award
};

export default function ChiSiamo() {
    const { data: contenuti = [] } = useQuery({
        queryKey: ['contenutoChiSiamo'],
        queryFn: () => base44.entities.ContenutoChiSiamo.filter({ visibile: true }, 'ordine')
    });

    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Chi Siamo - La Storia di Tecnosys | UPS Sicilia"
                description="Scopri la storia di Tecnosys: oltre 20 anni al servizio della continuità energetica in Sicilia. La nostra missione, valori e team."
                keywords="tecnosys, ups sicilia, storia aziendale, team, missione"
            />

            {/* Hero */}
            <section className="relative bg-gray-900 pt-32 pb-20">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=80"
                        alt="Team Tecnosys"
                        className="w-full h-full object-cover opacity-20"
                    />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Badge className="bg-blue-600 text-white mb-6">Chi Siamo</Badge>
                        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                            Tecnosys di Lo Verso Salvatore
                        </h1>
                        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                            Da oltre 20 anni proteggiamo le infrastrutture critiche della Sicilia
                            con soluzioni UPS professionali e assistenza di eccellenza.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Content Sections */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Static Content - Always Visible */}
                    <div className="grid lg:grid-cols-2 gap-16 mb-20">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">La Nostra Storia</h2>
                            <div className="prose prose-lg text-gray-600">
                                <p>
                                    Tecnosys nasce dalla passione e dall'esperienza di Salvatore Lo Verso,
                                    professionista con oltre 20 anni nel settore della protezione energetica.
                                </p>
                                <p>
                                    Dal 2003 operiamo in tutta la Sicilia, specializzandoci nella vendita,
                                    installazione e manutenzione di gruppi di continuità (UPS) per infrastrutture critiche.
                                </p>
                            </div>
                        </div>
                        <div>
                            <img
                                src="/images/storia-ups.jpg"
                                alt="UPS Systems"
                                className="rounded-2xl shadow-xl"
                                loading="eager"
                            />
                        </div>
                    </div>

                    {/* Dynamic Content */}
                    {contenuti.length > 0 && (
                        <div className="space-y-20">
                            {contenuti.map((contenuto, index) => {
                                const Icon = iconMap[contenuto.sezione] || Shield;
                                const isEven = index % 2 === 0;

                                return (
                                    <motion.div
                                        key={contenuto.id}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.2 }}
                                        className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven && 'lg:flex-row-reverse'}`}
                                    >
                                        <div className={isEven ? '' : 'lg:order-2'}>
                                            {contenuto.immagine ? (
                                                <img
                                                    src={contenuto.immagine}
                                                    alt={contenuto.titolo}
                                                    className="rounded-2xl shadow-xl w-full"
                                                />
                                            ) : (
                                                <div className="aspect-square rounded-2xl bg-blue-100 flex items-center justify-center">
                                                    <Icon className="w-24 h-24 text-blue-600" />
                                                </div>
                                            )}
                                        </div>
                                        <div className={isEven ? '' : 'lg:order-1'}>
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                                                    <Icon className="w-6 h-6 text-blue-600" />
                                                </div>
                                                <h2 className="text-3xl font-bold text-gray-900">{contenuto.titolo}</h2>
                                            </div>
                                            <div
                                                className="prose prose-lg max-w-none text-gray-600"
                                                dangerouslySetInnerHTML={{ __html: contenuto.contenuto }}
                                            />
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}